import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import CreationFormWrapper from "../../actprep-ui/components/CreationFormWrapper";
import { actSubjectsWithReview } from "../../actprep-ui/constants/program";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { usePost, usePut } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

export default function ProgramWeekForm({
  open,
  setOpen,
  initialValue,
  initialWeek,
  setValue,
}) {
  const editing = Boolean(initialWeek);
  const createProgramWeekMutation = usePost(apiUrls.admin.programWeeks());
  const editProgramWeekMutation = usePut(apiUrls.admin.programWeeks());
  const [formData, setFormData] = useState({
    title: initialWeek?.title || "",
    subject: initialWeek?.subject || "",
    visible_title: initialWeek?.visible_title || "",
    is_break_week: initialWeek?.is_break_week || false,
    is_fast_track: initialWeek?.is_fast_track || false,
    memorization_lesson: initialWeek?.memorization_lesson
      ? {
          label: initialWeek.memorization_lesson.name,
          uuid: initialWeek.memorization_lesson.uuid,
        }
      : null,
  });
  const handleFocus = () => setError(null);
  const [error, setError] = useState(null);
  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async () => {
    let response;
    if (editing) {
      response = await editProgramWeekMutation.mutateAsync({
        ...formData,
        memorization_lesson: formData.memorization_lesson?.uuid,
        program_week_uuid: initialWeek.uuid,
      });
    } else {
      response = await createProgramWeekMutation.mutateAsync({
        ...formData,
        memorization_lesson: formData.memorization_lesson?.uuid,
      });
    }
    const { data } = response;
    if (!data.success) {
      setError(data.reason);
      return;
    }
    setValue({
      uuid: data.program_week.uuid,
      label: data.program_week.__str__,
    });
    setOpen(false);
    if (editing) {
      showSuccessSnackbar("Program Week Updated!");
      return;
    }
    showSuccessSnackbar("New Program Week Created!");
  };

  useEffect(() => {
    if (open) {
      setFormData((formData) => ({
        ...formData,
        title: initialValue,
      }));
    }
  }, [open, initialValue]);

  return (
    <CreationFormWrapper
      open={open}
      onClose={handleClose}
      onSubmit={onSubmit}
      error={error}
      title={
        editing
          ? `Editing '${initialWeek.title}' Program Week`
          : "Add a new Program Week"
      }
      confirmText={editing ? "Save" : "Create"}
    >
      <Grid container gap={3} marginTop={1}>
        <Grid size={12}>
          <TextField
            fullWidth
            autoFocus
            required
            value={formData.title}
            onChange={(event) =>
              setFormData({
                ...formData,
                title: event.target.value,
              })
            }
            label="Title"
            type="text"
            variant="standard"
            onFocus={handleFocus}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            autoFocus
            value={formData.visible_title || ""}
            onChange={(event) =>
              setFormData({
                ...formData,
                visible_title: event.target.value,
              })
            }
            label="Student-Visible Title"
            type="text"
            variant="standard"
            onFocus={handleFocus}
          />
        </Grid>
        <Grid size={6}>
          <FormControl
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_break_week || false}
                  onChange={(event, checked) => {
                    setFormData((formData) => {
                      formData.is_break_week = checked;
                      return { ...formData };
                    });
                  }}
                  color="secondary"
                />
              }
              label="Break week"
            />
          </FormControl>
        </Grid>
        {!formData.is_break_week && (
          <>
            <Grid size={5}>
              <FormControl
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_fast_track || false}
                      onChange={(event, checked) => {
                        setFormData((formData) => {
                          formData.is_fast_track = checked;
                          return { ...formData };
                        });
                      }}
                      color="secondary"
                    />
                  }
                  label="Fast Track"
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth variant="standard">
                <InputLabel>Subject</InputLabel>
                <Select
                  value={formData.subject || ""}
                  label="Subject"
                  required
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      subject: event.target.value,
                    })
                  }
                  onFocus={handleFocus}
                >
                  {actSubjectsWithReview.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {capitalize(subject)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <ModelAutoComplete
                modelName={"Lesson"}
                label={"Memorization Lesson"}
                variant="standard"
                value={formData.memorization_lesson}
                required
                setValue={(newValue) => {
                  setFormData({
                    ...formData,
                    memorization_lesson: newValue,
                  });
                }}
                onFocus={handleFocus}
              />
            </Grid>
          </>
        )}
      </Grid>
    </CreationFormWrapper>
  );
}
